import { logWarn } from '@classic/Shared/Logger';
import type { PopupAlert } from '@classic/Components/PopUpAlerts/Schemas';

const STORAGE_KEY = 'ss-popup-alerts-viewed';
const VIEWED_TTL_MS = 24 * 60 * 60 * 1000;
const LOG_CONTEXT = 'PopUpAlerts';

type ViewedMap = Record<string, number>;

/**
 * Tracks which alerts the current browser has already seen. Storage access
 * is guarded throughout — Safari private mode and quota errors fail open
 * (alerts are treated as unviewed) rather than breaking the page.
 */
class ViewedAlertsStore {
    public getUnviewed(alerts: readonly PopupAlert[]): PopupAlert[] {
        const viewed = this.readAndPrune();
        return alerts.filter((alert) => !(String(alert.id) in viewed));
    }

    public markViewed(alertIds: readonly number[]): void {
        if (alertIds.length === 0) {
            return;
        }
        const viewed = this.readAndPrune();
        const now = Date.now();
        alertIds.forEach((id) => {
            viewed[String(id)] = now;
        });
        this.write(viewed);
    }

    private readAndPrune(): ViewedMap {
        const viewed = this.read();
        const now = Date.now();
        const pruned: ViewedMap = {};
        let hasExpiredEntries = false;

        Object.entries(viewed).forEach(([id, viewedAt]) => {
            if (now - viewedAt < VIEWED_TTL_MS) {
                pruned[id] = viewedAt;
            } else {
                hasExpiredEntries = true;
            }
        });

        if (hasExpiredEntries) {
            this.write(pruned);
        }

        return pruned;
    }

    private read(): ViewedMap {
        try {
            const raw = window.localStorage.getItem(STORAGE_KEY);
            if (!raw) {
                return {};
            }
            const parsed = JSON.parse(raw);
            return this.isViewedMap(parsed) ? parsed : {};
        } catch (error) {
            logWarn(LOG_CONTEXT, 'Unable to read viewed alerts from localStorage', error);
            return {};
        }
    }

    private write(viewed: ViewedMap): void {
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(viewed));
        } catch (error) {
            logWarn(LOG_CONTEXT, 'Unable to persist viewed alerts to localStorage', error);
        }
    }

    private isViewedMap(value: unknown): value is ViewedMap {
        return typeof value === 'object'
            && value !== null
            && !Array.isArray(value)
            && Object.values(value as Record<string, unknown>).every((entry) => typeof entry === 'number');
    }
}

export default ViewedAlertsStore;
